// Clinician counterpart of MySummaries.js — the plain-language AI summary
// list, kept SEPARATE from MyPatientsResults.js (the structured test-value
// page) per explicit user instruction, 2026-10-22: the two were previously
// mixed into one page (MyPatientsResults.js used to also render
// ai_summary). This page owns ai_summary + the stats/structured "dial"
// content; MyPatientsResults.js keeps clinical_flags/citations/
// followup_test_suggestions as its own CDS-exclusive content.
//
// Reads GET /my-patients-summaries (backend, this same app), which uses
// the SAME two-path clinician-identity match as /my-patients-results
// (clinician_sub, or NPI+hash-confirmed — a shared-roster patient match
// was removed 2026-10-23 per explicit user instruction) — covers every
// report-generation path uniformly (digital order, paper requisition,
// e-requisition, direct report upload), not just Order-a-Test.
//
// StructuredResultsSection/ProoveStatsSection/GenotypesList/etc. below are
// duplicated from MyPatientsResults.js rather than imported — same
// "recreate rather than cross-import two separate role-specific pages"
// convention this file's own sibling already uses for statusKeyFor/
// statusColor.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Chip, Alert, TextField, MenuItem, Select, InputAdornment } from "@mui/material";
import { ArrowBack, PersonOutline, Science, Assignment, Biotech, HourglassEmpty, Search } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import UserContext from "./UserContext";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import { formatEST } from "../dateUtils";
import { displayNameForTestId } from "../testDisplayNames";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
// "Summaries" gate — its own flag, deliberately separate from
// REACT_APP_MY_RESULTS_ENABLED (the "test results" gate), per explicit
// user instruction: two distinct flags, one per content family.
const MY_PATIENTS_SUMMARIES_ENABLED = process.env.REACT_APP_MY_SUMMARIES_ENABLED === "true";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

function statusKeyFor(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("critical")) return "critical";
  if (s.includes("high")) return "high";
  if (s.includes("low")) return "low";
  if (s.includes("abnormal") || s.includes("detected")) return "high";
  return "normal";
}
function statusColor(key, isDark) {
  if (key === "normal") return isDark ? "#4ade80" : "#1a7a1a";
  if (key === "critical") return "#991b1b";
  return "#cc0000";
}

function SectionHeader({ icon, label, isDark }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
      {icon}
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b" }}>{label}</Typography>
    </Box>
  );
}

function StaticValueRow({ row, isDark }) {
  const key = statusKeyFor(row.status);
  const color = statusColor(key, isDark);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, px: 1.5 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b" }}>{row.test}</Typography>
        <Typography sx={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }}>
          {row.value} {row.unit || ""} {row.reference_range ? `· Ref: ${row.reference_range}` : ""}
        </Typography>
      </Box>
      <Chip size="small" label={row.status || "—"} sx={{ fontSize: 11, fontWeight: 700, bgcolor: `${color}1a`, color }} />
    </Box>
  );
}

function toxSeverityKey(section) {
  const s = String(section || "").toUpperCase();
  if (s.includes("ILLICIT")) return "critical";
  if (s.includes("INCONSISTENT")) return "high";
  if (s.includes("NOT TESTED")) return "normal";
  return "normal";
}

function StructuredResultsSection({ structured, isDark }) {
  if (!structured) return null;
  if (structured.kind === "standard") {
    const rows = structured.full_results || [];
    if (!rows.length) return null;
    return <Box>{rows.map((row, i) => <StaticValueRow key={`${row.test}-${i}`} row={row} isDark={isDark} />)}</Box>;
  }
  const rows = structured.kind === "tox"
    ? [...(structured.first_page_summary || []), ...(structured.second_section || [])]
    : (structured.consistent || []);
  const realRows = rows.filter((r) => {
    const label = r?.["Drug Prescribed"] ?? r?.["Analyte Detected"] ?? r?.Drug;
    return label != null && String(label).trim() !== "" && label !== "N/A" && label !== "__EMPTY__";
  });
  const validity = structured.kind === "drug_screen" && Array.isArray(structured.validity) ? structured.validity : [];
  if (!realRows.length && !validity.length) return null;
  return (
    <Box>
      {realRows.map((r, i) => {
        const label = r["Drug Prescribed"] ?? r["Analyte Detected"] ?? r.Drug;
        const key = structured.kind === "tox" ? toxSeverityKey(r.Section)
          : (String(r.Section || "").toUpperCase().includes("INCONSISTENT") ? "high" : "normal");
        const color = statusColor(key, isDark);
        return (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, px: 1.5 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b" }}>{label}</Typography>
              {r.Section && <Typography sx={{ fontSize: 11.5, color: isDark ? "#94a3b8" : "#64748b" }}>{r.Section}</Typography>}
            </Box>
            <Chip size="small" label={r["Result (ng/mL)"] || r.Result || "—"} sx={{ fontSize: 11, fontWeight: 700, bgcolor: `${color}1a`, color }} />
          </Box>
        );
      })}
      {validity.length > 0 && (
        <Box sx={{ mt: realRows.length ? 1.5 : 0 }}>
          <SectionHeader icon={<Science sx={{ fontSize: 16, color: BRAND.gold }} />} label="Specimen validity" isDark={isDark} />
          {validity.map((v, i) => {
            const key = v.Flagged ? "high" : "normal";
            const color = statusColor(key, isDark);
            return (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, px: 1.5 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b", flex: 1 }}>
                  {v["Validity Test"]}
                </Typography>
                <Chip size="small" label={v["Test Outcome"] || "—"} sx={{ fontSize: 11, fontWeight: 700, bgcolor: `${color}1a`, color }} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function GenotypesList({ genotypes, isDark }) {
  if (!Array.isArray(genotypes) || !genotypes.length) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <SectionHeader icon={<Biotech sx={{ fontSize: 16, color: BRAND.gold }} />} label={`Gene findings (${genotypes.length})`} isDark={isDark} />
      {genotypes.map((g, i) => (
        <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
          <b>{g?.gene}</b>: {g?.result || "—"}{g?.rs_number ? ` (${g.rs_number})` : ""}
        </Typography>
      ))}
    </Box>
  );
}

function ForeignScoresList({ scores, isDark }) {
  if (!Array.isArray(scores) || !scores.length) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <SectionHeader icon={<Science sx={{ fontSize: 16, color: BRAND.gold }} />} label="Additional scores" isDark={isDark} />
      {scores.map((s, i) => (
        <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
          <b>{s?.label}:</b> {s?.value ?? "—"}
        </Typography>
      ))}
    </Box>
  );
}

function HistoryQuestionsList({ items, isDark }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <SectionHeader icon={<Assignment sx={{ fontSize: 16, color: BRAND.gold }} />} label="History questionnaire" isDark={isDark} />
      {items.map((q, i) => (
        <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
          <b>{q?.question}:</b> {q?.answer ?? "—"}
        </Typography>
      ))}
    </Box>
  );
}

function ProoveStatsSection({ stats, rtid, isDark }) {
  if (!stats) return null;
  if (rtid === "proove_pgx") {
    const genes = Array.isArray(stats.genes) ? stats.genes : [];
    const riskMgmt = Array.isArray(stats.risk_management) ? stats.risk_management : [];
    const hasAnything = genes.length || riskMgmt.length || stats.current_patient_medications || stats.dosing_guidance_summary;
    if (!hasAnything) {
      return (
        <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#94a3b8", py: 1, textAlign: "center" }}>
          No drug-metabolism findings were extracted from this report.
        </Typography>
      );
    }
    return (
      <Box>
        {genes.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <SectionHeader icon={<Biotech sx={{ fontSize: 16, color: BRAND.gold }} />} label={`Gene findings (${genes.length})`} isDark={isDark} />
            {genes.map((g, i) => (
              <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
                <b>{g.gene}</b>: {g.genotype || "—"}{g.phenotype ? ` (${g.phenotype})` : ""}
              </Typography>
            ))}
          </Box>
        )}
        {stats.current_patient_medications && (
          <Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 1 }}>
            <b>Flagged medications:</b> {stats.current_patient_medications}
          </Typography>
        )}
        {riskMgmt.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <SectionHeader icon={<Science sx={{ fontSize: 16, color: BRAND.gold }} />} label="Risk management findings" isDark={isDark} />
            {riskMgmt.map((r, i) => (
              <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
                <b>{r.condition}:</b> {r.finding}
              </Typography>
            ))}
          </Box>
        )}
        {stats.dosing_guidance_summary && (
          <Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155" }}>
            <b>Dosing guidance:</b> {stats.dosing_guidance_summary}
          </Typography>
        )}
      </Box>
    );
  }
  const scoreFields = rtid === "proove_opioid_risk"
    ? [["proove_ori", "Opioid Risk Index"], ["ort", "ORT"], ["phq2", "PHQ-2"]]
    : [["self_reported_pain", "Self-Reported Pain"], ["pss", "PSS"]];
  const present = scoreFields.filter(([f]) => stats[f] && (stats[f].score != null || stats[f].level));
  const hasComt = rtid === "proove_pain_perception" && !!stats.comt_predisposition?.level;
  const hasAnything = present.length || hasComt || stats.genotypes?.length || stats.foreign_scores?.length
    || (rtid === "proove_opioid_risk" && stats.history_questions?.length);
  if (!hasAnything) {
    return (
      <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#94a3b8", py: 1, textAlign: "center" }}>
        No findings were extracted from this report.
      </Typography>
    );
  }
  return (
    <Box>
      {present.map(([f, label]) => (
        <Typography key={f} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
          <b>{label}:</b> {stats[f].score ?? ""}{stats[f].level ? ` (${stats[f].level})` : ""}
        </Typography>
      ))}
      {hasComt && (
        <Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 1.5 }}>
          <b>Genetic Pain Sensitivity (COMT):</b> {stats.comt_predisposition.level}
        </Typography>
      )}
      <GenotypesList genotypes={stats.genotypes} isDark={isDark} />
      <ForeignScoresList scores={stats.foreign_scores} isDark={isDark} />
      {rtid === "proove_opioid_risk" && <HistoryQuestionsList items={stats.history_questions} isDark={isDark} />}
    </Box>
  );
}

const _PROOVE_TYPED_IDS = new Set(["proove_pgx", "proove_opioid_risk", "proove_pain_perception"]);

function PatientSummaryCard({ result, isDark }) {
  const structured = result.structured_results;
  const stats = result.stats_json;
  const rtid = result.report_type_id;
  const isProoveTyped = _PROOVE_TYPED_IDS.has(rtid);
  const patientName = [result.patient_first_name, result.patient_last_name].filter(Boolean).join(" ") || "Unknown patient";

  return (
    <Box sx={{
      borderRadius: 3, overflow: "hidden", mb: 3,
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.1)",
      bgcolor: isDark ? "#111827" : "#fff",
      boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,53,122,0.06)",
    }}>
      <Box sx={{
        px: { xs: 2, sm: 3 }, py: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
        background: `linear-gradient(135deg, ${BRAND.navyDark} 0%, ${BRAND.navy} 100%)`,
      }}>
        <PersonOutline sx={{ color: BRAND.gold, fontSize: 18 }} />
        <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: 14.5 }}>{patientName}</Typography>
        <Chip label={String(rtid || "").replace(/_/g, " ")} size="small"
          sx={{ textTransform: "capitalize", bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontSize: "0.68rem",
            maxWidth: { xs: "55%", sm: "40%" } }} />
        {stats?.is_proove_branded && (
          <Chip label="Proove Genomics" size="small" sx={{ bgcolor: "rgba(201,169,110,0.28)", color: "#fff", fontSize: "0.62rem", fontWeight: 700 }} />
        )}
        {result.generated_at && (
          <Chip label={formatEST(result.generated_at, { dateOnly: true })} size="small"
            sx={{ ml: "auto", bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontSize: "0.68rem" }} />
        )}
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {result.patient_dob && (
          <Typography sx={{ fontSize: 12.5, color: isDark ? "#94a3b8" : "#64748b", mb: 2 }}>
            DOB: {result.patient_dob}
          </Typography>
        )}

        {result.stats_extraction_status === "pending" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 2, color: isDark ? "#94a3b8" : "#64748b" }}>
            <HourglassEmpty sx={{ fontSize: 20, opacity: 0.7 }} />
            <Typography sx={{ fontStyle: "italic", fontSize: "0.9rem" }}>
              This summary is still being generated — check back shortly.
            </Typography>
          </Box>
        ) : result.ai_summary ? (
          <Box sx={{ mb: 2.5, fontSize: 14, lineHeight: 1.65, color: isDark ? "#cbd5e1" : "#334155", "& p": { mb: 1.5 }, "& ul": { pl: 3 } }}
            dangerouslySetInnerHTML={{ __html: result.ai_summary }} />
        ) : (
          <Typography sx={{ color: isDark ? "#64748b" : "#AAA", fontStyle: "italic", mb: 2 }}>
            Summary not yet available for this report.
          </Typography>
        )}

        {isProoveTyped ? (
          <ProoveStatsSection stats={stats} rtid={rtid} isDark={isDark} />
        ) : (
          <StructuredResultsSection structured={structured} isDark={isDark} />
        )}
      </Box>
    </Box>
  );
}

export default function MyPatientsSummaries() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const token = user?.token;
  const [results, setResults] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null);

  // 2026-10-26 addition (user-directed — "clinicians should be able to
  // search for patients, report types, dates, date of births") — see
  // MyPatientsResults.js's own identical addition for the full
  // rationale; same client-side-filter-over-the-already-fetched-roster
  // pattern, kept consistent between these two sibling pages.
  const [searchTerm, setSearchTerm] = useState("");
  const [reportTypeFilter, setReportTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const reportTypeOptions = useMemo(
    () => Array.from(new Set(results.map((r) => r.report_type_id).filter(Boolean))).sort(),
    [results],
  );

  const filteredResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return results.filter((r) => {
      if (reportTypeFilter && r.report_type_id !== reportTypeFilter) return false;
      if (dateFrom && r.generated_at && r.generated_at < dateFrom) return false;
      if (dateTo && r.generated_at && r.generated_at > `${dateTo}T23:59:59`) return false;
      if (!term) return true;
      const name = [r.patient_first_name, r.patient_last_name].filter(Boolean).join(" ").toLowerCase();
      const rtidHuman = displayNameForTestId(r.report_type_id).toLowerCase();
      const dob = String(r.patient_dob || "").toLowerCase();
      return name.includes(term) || rtidHuman.includes(term) || dob.includes(term);
    });
  }, [results, searchTerm, reportTypeFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (!MY_PATIENTS_SUMMARIES_ENABLED) {
      setErrorState({ message: "My Patients' Summaries isn't available yet.", severity: "info" });
      setLoading(false);
      return;
    }
    axios.get(`${API_BASE}/my-patients-summaries`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data?.feature_disabled) {
          setErrorState({ message: "My Patients' Summaries isn't available yet.", severity: "info" });
          return;
        }
        setResults(res.data.results || []);
        setTruncated(!!res.data.truncated);
      })
      .catch(() => setErrorState({ message: "Unable to load your patients' summaries right now.", severity: "error" }))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f8f6f2" }}>
      <Box sx={{
        px: 3, py: 2, bgcolor: isDark ? "#111827" : "#fff",
        borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)",
        display: "flex", alignItems: "center", gap: 2,
      }}>
        <Box onClick={() => navigate("/home")} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/home"); } }}
          sx={{
            display: "flex", alignItems: "center", gap: 0.5,
            color: isDark ? "#94a3b8" : "#64748b", fontSize: 14.5, cursor: "pointer",
            borderRadius: "8px", px: 1.2, py: 0.6, transition: "all 0.2s",
            "&:hover": { color: isDark ? BRAND.gold : BRAND.navy, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)" },
            "&:focus-visible": { outline: `2px solid ${BRAND.gold}`, outlineOffset: 2 },
          }}
        >
          <ArrowBack sx={{ fontSize: 15 }} />
          <span>Home</span>
        </Box>
        <Box sx={{ width: "1px", height: 20, bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,122,0.1)" }} />
        <Typography sx={{ fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, fontSize: 18, flex: 1 }}>My Patients' Summaries</Typography>
        <ThemeToggle />
        <NavUserMenu />
      </Box>

      <Box sx={{ maxWidth: 820, mx: "auto", px: 3, py: 5 }}>
        <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", maxWidth: 520, mb: 4 }}>
          A plain-language summary of each of your patients' completed tests, as soon as they're ready.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>
        ) : errorState ? (
          <Alert severity={errorState.severity} sx={{ borderRadius: 2 }}>{errorState.message}</Alert>
        ) : results.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
            <Science sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography>No summaries yet for your patients.</Typography>
          </Box>
        ) : (
          <>
            {truncated && (
              <Typography sx={{ fontSize: 12.5, color: isDark ? "#94a3b8" : "#64748b", mb: 2, fontStyle: "italic" }}>
                Showing the 100 most recent summaries.
              </Typography>
            )}
            <Box sx={{
              display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3, p: 2, borderRadius: 2,
              bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,53,122,0.03)",
              border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)",
            }}>
              <TextField size="small" placeholder="Search patient, DOB, or report type…"
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flex: "1 1 220px", minWidth: 200 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: isDark ? "#64748b" : "#94a3b8" }} /></InputAdornment> }} />
              <Select size="small" displayEmpty value={reportTypeFilter}
                onChange={(e) => setReportTypeFilter(e.target.value)} sx={{ minWidth: 170 }}>
                <MenuItem value="">All report types</MenuItem>
                {reportTypeOptions.map((rtid) => (
                  <MenuItem key={rtid} value={rtid}>{displayNameForTestId(rtid)}</MenuItem>
                ))}
              </Select>
              <TextField size="small" label="From" type="date" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
              <TextField size="small" label="To" type="date" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
            </Box>
            {filteredResults.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
                <Search sx={{ fontSize: 32, mb: 1, opacity: 0.4 }} />
                <Typography>No summaries match these filters.</Typography>
              </Box>
            ) : (
              filteredResults.map((r) => <PatientSummaryCard key={r._id} result={r} isDark={isDark} />)
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
